import { useQuery } from '@tanstack/react-query';
import { fetchDucklings } from '@/api/endpoints';
import { formatRelativeTime } from '@/lib/format';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/states';
import { asQueryError } from '@/query/execute';
import type { DucklingState } from '@/types/api';

const STATE_COLOR: Record<DucklingState, string> = {
  STARTING: 'var(--c-remote)',
  IDLE: 'var(--c-ok)',
  BUSY: 'var(--c-local)',
  COOLDOWN: 'var(--c-faint)',
  TERMINATING: 'var(--c-danger)',
};

/**
 * Memory cap for one duckling.
 *
 * Guarded rather than divided blindly: when the service omitted this field the
 * panel rendered "NaN GB" on every row, which reads as a broken fleet rather
 * than a broken payload. A dash is at least honest about knowing nothing.
 */
function formatMemory(mb: number | undefined): string {
  if (typeof mb !== 'number' || !Number.isFinite(mb)) return '—';
  return mb >= 1024 ? `${(mb / 1024).toFixed(0)} GB` : `${mb} MB`;
}

/** Read-only view of the duckling fleet (spec §4.3). */
export function FleetPanel(): JSX.Element {
  const fleet = useQuery({
    queryKey: ['ducklings'],
    queryFn: fetchDucklings,
    refetchInterval: 10_000,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="fleet-panel">
      <div className="panel-head">
        <span className="eyebrow">Ducklings</span>
        <span className="tnum text-[10px] text-faint">{fleet.data?.length ?? 0}</span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {fleet.isLoading ? <LoadingState label="Loading fleet" /> : null}
        {fleet.error ? (
          <ErrorState error={asQueryError(fleet.error)} onRetry={() => void fleet.refetch()} />
        ) : null}
        {fleet.data?.length === 0 ? (
          <EmptyState title="No ducklings running" hint="The supervisor starts one on your next query." />
        ) : null}

        {(fleet.data ?? []).map((duckling) => (
          <div key={duckling.id} className="border-b border-line px-2.5 py-1.5">
            <div className="flex items-center gap-1.5">
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: STATE_COLOR[duckling.state] }}
                aria-hidden="true"
              />
              <span className="font-mono text-[11px] text-ink">{duckling.id}</span>
              <span className="eyebrow ml-auto">{duckling.state}</span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2.5 text-[10px] text-faint">
              <span className="tnum">{duckling.tier}</span>
              <span className="tnum">{formatMemory(duckling.memoryLimitMb)}</span>
              <span className="truncate">{duckling.principalHash ?? 'unassigned'}</span>
              <span className="tnum ml-auto">up {formatRelativeTime(duckling.startedAt)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
